import { makeAutoObservable } from 'mobx'
import { createContext, useContext } from 'react'
import { IStoreCard } from '../../services/MultiSellerServicesProvider'
import { MiscPreferencesKeys } from '../user/UserStore'
import { StoreInstances } from '../StoreInstancesContainer'

export class SellerStore {
  private _sellers: IStoreCard[]

  constructor() {
    makeAutoObservable(this)
  }

  public getSellers = (): IStoreCard[] => {
    return this._sellers
  }

  public setSellers = (sellers: IStoreCard[]): void => {
    this._sellers = sellers
  }

  public select = (seller: IStoreCard): void => {
    this._sellers.forEach((s) => (s.selected = s.id === seller.id))
  }

  public getSelectedSeller = (): IStoreCard => {
    return this._sellers?.find((s) => s.selected)
  }

  public prioritizeMyPlace = (a: IStoreCard, _b: IStoreCard): number => {
    if (a.isMyPlaceStore) return -1
    return 0
  }

  public prioritizeSelected = (a: IStoreCard, _b: IStoreCard): number => {
    if (a.selected) return -1
    return 0
  }

  public sort = (): void => {
    const sortedCards = this.sortByUserPref(this._sellers)
    sortedCards.sort(this.prioritizeSelected).sort(this.prioritizeMyPlace)
    this._sellers = sortedCards
  }

  public update = (seller: IStoreCard): void => {
    this._sellers.filter((s) => s.id === seller.id).map((_s) => seller)
  }

  sortStoreCardsByIds = (
    storeCards: IStoreCard[],
    order: string[]
  ): IStoreCard[] => {
    // Create a map for the index positions of each id in 'order'
    const orderMap = new Map<string, number>(
      order.map((id, index) => [id, index])
    )

    const sortedCards = storeCards.sort((a, b) => {
      const indexA = orderMap.get(a.id?.toString()) ?? Infinity
      const indexB = orderMap.get(b.id?.toString()) ?? Infinity

      return indexA - indexB
    })

    return sortedCards
  }

  sortByUserPref = (cards: IStoreCard[]): IStoreCard[] => {
    const attrKey = MiscPreferencesKeys.MULTI_SELLER_IDS_SORT_ORDER
    const orderString = StoreInstances?.userStore?.miscPreferences?.[attrKey]

    if (!orderString) return cards

    const order = JSON.parse(orderString)
    return this.sortStoreCardsByIds(cards, order)
  }

  saveSellerOrder = (sellers: IStoreCard[]): void => {
    this._sellers = sellers

    const ids = sellers.map((s) => s.id?.toString())
    const attrKey = MiscPreferencesKeys.MULTI_SELLER_IDS_SORT_ORDER
    StoreInstances?.userStore?.saveMiscPreference(attrKey, ids)
  }
}

export const SellerStoreContext = createContext<SellerStore>(undefined)

export const useSellerStore = (): SellerStore => {
  const sellerStoreContext = useContext(SellerStoreContext)
  if (!sellerStoreContext) {
    throw new Error(
      'No SellerContext.Provider found when calling useSellerStore.'
    )
  }
  return sellerStoreContext
}
